import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Close,
  LocationOn,
  Schedule,
  Engineering,
  BugReport,
  Thermostat,
  WaterDrop,
  Air,
  Compress,
  WbSunny,
  Speed,
  Fullscreen,
  FullscreenExit,
  Refresh,
  Download
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { stationService, stationTimeseriesService, measurementsService } from '../services/api';
import { convertToCSV, downloadCSV, generateCSVFilename } from '../utils/csvExport';
import StationIngestionInfo from './StationIngestionInfo';
import { SUPPORTED_METRICS } from '../config/sensorMetrics';

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  id: string;
  uuid: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  location: Location;
  elevation?: number;
  notes?: string;
  owner?: string;
  public: boolean;
  source?: string;
  timezone?: string;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  api_key?: string;
  mqtt_username?: string;
  user_email?: string;
}

interface StationDetailsProps {
  open: boolean;
  onClose: () => void;
  stationUuid?: string;
  initialTab?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`station-tabpanel-${index}`}
    aria-labelledby={`station-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const getSensorIcon = (sensorType: string) => {
  switch (sensorType.toLowerCase()) {
    case 'temperature': return <Thermostat color="primary" />;
    case 'humidity':
    case 'rain':
    case 'reference_et': return <WaterDrop color="primary" />;
    case 'wind_speed': return <Air color="primary" />;
    case 'atmospheric_pressure': return <Compress color="primary" />;
    case 'solar_radiation': return <WbSunny color="primary" />;
    default: return <Speed color="primary" />;
  }
};

const formatSensorName = (key: string) => {
  const sensorNames: Record<string, string> = {
    temperature: 'Temperature',
    humidity: 'Humidity',
    rain: 'Rainfall',
    wind_speed: 'Wind Speed',
    wind_direction: 'Wind Direction',
    atmospheric_pressure: 'Atmospheric Pressure',
    reference_et: 'Reference ET',
    solar_radiation: 'Solar Radiation'
  };
  return sensorNames[key] || key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ');
};

const StationDetails: React.FC<StationDetailsProps> = ({ open, onClose, stationUuid, initialTab = 0 }) => {
  const [station, setStation] = useState<Station | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(initialTab);
  const [timeRange, setTimeRange] = useState<number>(24);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [liveReadings, setLiveReadings] = useState<any[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  useEffect(() => {
    if (open && stationUuid) {
      fetchStationDetails();
      fetchTimeSeriesData();
    }
  }, [open, stationUuid]);

  useEffect(() => {
    if (open && stationUuid && tabValue === 2) {
      fetchTimeSeriesData();
    }
  }, [timeRange]);

  useEffect(() => {
    setTabValue(initialTab);
  }, [initialTab]);

  // Auto-refresh live readings every 1 minute when Live Readings tab is active
  useEffect(() => {
    if (open && station && tabValue === 1) {
      // Initial fetch
      fetchLiveReadings();

      // Set up interval for auto-refresh
      const interval = setInterval(() => {
        fetchLiveReadings();
      }, 60000); // 60 seconds (1 minute)

      // Cleanup interval on unmount or when tab/dialog changes
      return () => clearInterval(interval);
    }
  }, [open, station, tabValue]);

  const fetchStationDetails = async () => {
    if (!stationUuid) return;
    
    setLoading(true);
    try {
      const data = await stationService.getById(stationUuid);
      setStation(data);
    } catch (error) {
      console.error('Error fetching station details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSeriesData = async () => {
    if (!stationUuid) return;

    try {
      const data = await stationTimeseriesService.getTimeseries(stationUuid, timeRange);
      // Format data for charts
      const formattedData = data.timeseries.map((point: any) => ({
        ...point,
        time: new Date(point.timestamp).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })
      }));
      setTimeSeriesData(formattedData);
    } catch (error) {
      console.error('Error fetching time series data:', error);
    }
  };

  const fetchLiveReadings = async () => {
    if (!station?.id) return;

    try {
      // Use the efficient measurements/latest endpoint which fetches only the most recent record
      const data = await measurementsService.getLatest(parseInt(station.id), 1);

      if (data && data.length > 0) {
        // Get the latest (and only) reading
        const latest = data[0];

        // Convert to array of readings for display
        const readings = Object.keys(latest)
          .filter(key => key !== 'timestamp' && latest[key] !== null && latest[key] !== undefined)
          .map(key => ({
            metric: key,
            value: latest[key],
            timestamp: latest.timestamp,
            name: metricConfig[key]?.name || formatSensorName(key),
            unit: metricConfig[key]?.unit || '',
            color: metricConfig[key]?.color || '#00d4ff'
          }));

        setLiveReadings(readings);
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.error('Error fetching live readings:', error);
    }
  };

  // Get available metrics from the data (excluding timestamp and time)
  const getAvailableMetrics = () => {
    if (timeSeriesData.length === 0) return [];

    const firstPoint = timeSeriesData[0];
    const metrics = Object.keys(firstPoint).filter(
      key => key !== 'timestamp' && key !== 'time' && firstPoint[key] !== null && firstPoint[key] !== undefined
    );

    return metrics;
  };

  // Metric display configuration
  // Use centralized metric configuration
  const metricConfig = React.useMemo(() => {
    const config: Record<string, { name: string; color: string; unit: string }> = {};
    SUPPORTED_METRICS.forEach(metric => {
      config[metric.internal] = {
        name: metric.userFriendly.charAt(0).toUpperCase() + metric.userFriendly.slice(1).replace(/_/g, ' '),
        color: metric.color,
        unit: metric.unit
      };
    });
    return config;
  }, []);

  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value);
  };

  const getTimeRangeLabel = () => {
    if (timeRange === 24) return '24-Hour';
    if (timeRange === 48) return '48-Hour';
    if (timeRange === 120) return '5-Day';
    if (timeRange === 720) return 'Monthly';
    return `${timeRange}-Hour`;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!station) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 10001 }}>
        <DialogContent>
          <Typography>Loading station details...</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth sx={{ zIndex: 10001 }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" component="div">
              {station.name}
            </Typography>
            {station.uuid && (
              <Typography variant="body2" color="text.secondary">
                UUID: {station.uuid}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {station.debug && (
              <Chip icon={<BugReport />} label="Debug Mode" size="small" color="info" />
            )}
            <Chip
              label={station.status}
              size="small"
              color={station.status === 'Online' ? 'success' : station.status === 'Maintenance' ? 'warning' : 'error'}
            />
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="Station Info" />
          <Tab label="Live Readings" />
          <Tab label="Time Series" />
          <Tab label="Table View" />
          {/* Only show API & Integration tab if user owns the station (has uuid/api_key) */}
          {station.uuid && station.api_key && (
            <Tab label="API & Integration" />
          )}
        </Tabs>

        {/* Tab 0: Station Info */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.name}
              </Typography>
            </Grid>
            {/* Only show UUID if user owns the station */}
            {station.uuid && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  UUID
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {station.uuid}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.description || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.location.latitude.toFixed(6)}, {station.location.longitude.toFixed(6)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Elevation
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.elevation ? `${station.elevation} m` : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Timezone
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.timezone || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Owner
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.owner || station.user_email || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Source
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.source || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Visibility
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.public ? 'Public' : 'Private'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.status}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(station.created_at).toLocaleString('en-GB')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Updated At
              </Typography>
              <Typography variant="body1" gutterBottom>
                {station.updated_at ? new Date(station.updated_at).toLocaleString('en-GB') : 'N/A'}
              </Typography>
            </Grid>
            {station.notes && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {station.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 1: Live Readings */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={isFullscreen ? {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.default',
            zIndex: 9999,
            p: 4,
            overflow: 'auto'
          } : {}}>
            {/* Header with Fullscreen and Refresh buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant={isFullscreen ? "h3" : "h6"} gutterBottom>
                  {station.name} - Live Readings
                </Typography>
                {lastRefresh && (
                  <Typography variant="caption" color="text.secondary">
                    Last updated: {lastRefresh.toLocaleString('en-GB')}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={fetchLiveReadings}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </Button>
              </Box>
            </Box>

            {/* Live Readings Grid */}
            {liveReadings.length > 0 ? (
              <Grid container spacing={isFullscreen ? 4 : 3}>
                {liveReadings.map((reading) => (
                  <Grid item xs={12} sm={isFullscreen ? 6 : 12} md={isFullscreen ? 4 : 6} lg={isFullscreen ? 3 : 4} key={reading.metric}>
                    <Card sx={{
                      height: '100%',
                      background: isFullscreen
                        ? `linear-gradient(135deg, ${reading.color}20 0%, ${reading.color}10 100%)`
                        : 'background.paper',
                      border: isFullscreen ? `2px solid ${reading.color}40` : 'none'
                    }}>
                      <CardContent sx={{
                        textAlign: 'center',
                        p: isFullscreen ? 4 : 2
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: isFullscreen ? 3 : 2
                        }}>
                          {getSensorIcon(reading.metric)}
                          <Typography
                            variant={isFullscreen ? "h4" : "h6"}
                            sx={{ ml: 1 }}
                          >
                            {reading.name}
                          </Typography>
                        </Box>
                        <Typography
                          variant={isFullscreen ? "h1" : "h3"}
                          sx={{
                            color: reading.color,
                            fontWeight: 700,
                            mb: 1
                          }}
                        >
                          {typeof reading.value === 'number' ? reading.value.toFixed(2) : reading.value}
                        </Typography>
                        <Typography
                          variant={isFullscreen ? "h5" : "body1"}
                          color="text.secondary"
                        >
                          {reading.unit}
                        </Typography>
                        {reading.timestamp && (
                          <Typography
                            variant={isFullscreen ? "body1" : "caption"}
                            color="text.secondary"
                            sx={{ mt: isFullscreen ? 2 : 1 }}
                          >
                            {new Date(reading.timestamp).toLocaleString('en-GB')}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">
                  No live readings available yet. Data will refresh every 10 seconds.
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: Time Series Charts */}
        <TabPanel value={tabValue} index={2}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">
                {getTimeRangeLabel()} Sensor Measurements
              </Typography>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="time-range-label">Time Range</InputLabel>
                <Select
                  labelId="time-range-label"
                  id="time-range-select"
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                  label="Time Range"
                  MenuProps={{
                    style: { zIndex: 10002 }
                  }}
                >
                  <MenuItem value={24}>Last 24 Hours</MenuItem>
                  <MenuItem value={48}>Last 48 Hours</MenuItem>
                  <MenuItem value={120}>Last 5 Days</MenuItem>
                  <MenuItem value={720}>Last Month</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {timeSeriesData.length > 0 ? (
              <Box>
                {(() => {
                  const availableMetrics = getAvailableMetrics();

                  // Default colors for metrics without configuration
                  const defaultColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a28bd4', '#58d4ff'];

                  return availableMetrics.map((metric, index) => {
                    // Use predefined config if available, otherwise create a default one
                    const config = metricConfig[metric] || {
                      name: metric.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                      color: defaultColors[index % defaultColors.length],
                      unit: ''
                    };

                    return (
                      <Box key={metric} sx={{ mb: index < availableMetrics.length - 1 ? 4 : 0 }}>
                        <Typography variant="h6" gutterBottom>
                          {config.name}
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={timeSeriesData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" />
                            <YAxis />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#333333',
                                border: '1px solid #555555',
                                borderRadius: '4px',
                                color: '#ffffff'
                              }}
                              labelStyle={{ color: '#ffffff' }}
                              itemStyle={{ color: '#ffffff' }}
                            />
                            <Line
                              type="monotone"
                              dataKey={metric}
                              stroke={config.color}
                              strokeWidth={2}
                              name={config.unit ? `${config.name} (${config.unit})` : config.name}
                              connectNulls={true}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    );
                  });
                })()}
              </Box>
            ) : (
              <Typography color="text.secondary">
                No time series data available for this station.
              </Typography>
            )}
          </Box>
        </TabPanel>

        {/* Tab 3: Table View with CSV Export */}
        <TabPanel value={tabValue} index={3}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Data Table
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Download />}
                sx={{ color: '#000000' }}
                onClick={() => {
                  if (timeSeriesData.length > 0 && station) {
                    const stationData = {
                      id: station.uuid || 'public',
                      name: station.name,
                      description: station.description,
                      timeseries: timeSeriesData,  // Pass all data dynamically
                      period_hours: timeRange,
                      data_points: timeSeriesData.length
                    };
                    const csv = convertToCSV(stationData);
                    // Use station name only if UUID not available (public station)
                    const filename = generateCSVFilename(station.name, station.uuid || station.name);
                    downloadCSV(csv, filename);
                  }
                }}
                disabled={timeSeriesData.length === 0}
              >
                Export to CSV
              </Button>
            </Box>

            {timeSeriesData.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      {getAvailableMetrics().map((metric) => {
                        const config = metricConfig[metric] || {
                          name: metric.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                          unit: ''
                        };
                        return (
                          <TableCell key={metric} align="right">
                            {config.name} {config.unit ? `(${config.unit})` : ''}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timeSeriesData.slice().reverse().map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{new Date(row.timestamp).toLocaleString('en-GB')}</TableCell>
                        {getAvailableMetrics().map((metric) => (
                          <TableCell key={metric} align="right">
                            {row[metric] !== null && row[metric] !== undefined ? row[metric].toFixed(2) : 'N/A'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No data available to display in table.
              </Typography>
            )}
          </Box>
        </TabPanel>

        {/* Tab 4: API & Integration - Only render if user owns station */}
        {station.uuid && station.api_key && (
          <TabPanel value={tabValue} index={4}>
            <StationIngestionInfo
              stationUuid={station.uuid}
              apiKey={station.api_key}
            />
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StationDetails;